import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch } from "../../lib/api";

interface School {
  id: string;
  name: string;
  partnershipStatus: string;
  primaryContactName: string | null;
  primaryContactPhone: string | null;
  city?: string | null;
  createdAt: string;
}
interface Camp {
  id: string;
  schoolId: string;
  scheduledDate: string;
  status: string;
  expectedChildren: number | null;
  actualChildren: number | null;
  phrsGenerated: number | null;
}

function Schools() {
  const [schools, setSchools] = useState<School[]>([]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    Promise.all([
      apiFetch<{ schools: School[] }>("/api/schools"),
      apiFetch<{ camps: Camp[] }>("/api/camps"),
    ])
      .then(([s, c]) => {
        setSchools(s.schools);
        setCamps(c.camps);
      })
      .catch((e: Error) => setErr(e.message));
  };
  useEffect(load, []);

  const createSchool = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreating(true);
    const f = new FormData(e.currentTarget);
    try {
      await apiFetch("/api/schools", {
        method: "POST",
        body: JSON.stringify({
          name: f.get("name"),
          primaryContactName: f.get("contactName") || undefined,
          primaryContactPhone: f.get("contactPhone") || undefined,
          partnershipStatus: f.get("status") ?? "prospect",
        }),
      });
      (e.target as HTMLFormElement).reset();
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setCreating(false);
    }
  };

  const createCamp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    try {
      await apiFetch("/api/camps", {
        method: "POST",
        body: JSON.stringify({
          schoolId: f.get("schoolId"),
          scheduledDate: f.get("scheduledDate"),
          expectedChildren: Number(f.get("expectedChildren")) || undefined,
        }),
      });
      (e.target as HTMLFormElement).reset();
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="panel">
        <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">
          Screen 8
        </div>
        <h1 className="text-2xl font-semibold mb-2">School Pipeline</h1>
        {err && <div className="text-red-600 text-sm mb-2">{err}</div>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="panel">
          <h2 className="font-medium text-sm mb-2">Schools ({schools.length})</h2>
          {schools.length === 0 ? (
            <div className="text-xs text-slate-400">No schools yet.</div>
          ) : (
            <ul className="space-y-1 text-sm">
              {schools.map((s) => (
                <li key={s.id} className="flex items-center justify-between">
                  <Link
                    to="/schools/$schoolId"
                    params={{ schoolId: s.id }}
                    className="text-slate-700 hover:underline"
                  >
                    {s.name}
                  </Link>
                  <span className="text-xs rounded bg-slate-100 px-1.5">
                    {s.partnershipStatus}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={createSchool} className="mt-4 border-t pt-3 grid gap-2 text-sm">
            <div className="text-xs font-medium text-slate-600">
              Add school (admin/manager only)
            </div>
            <input name="name" required placeholder="School name" className="border rounded px-2 py-1" />
            <input name="contactName" placeholder="Contact name" className="border rounded px-2 py-1" />
            <input name="contactPhone" placeholder="+91XXXXXXXXXX" className="border rounded px-2 py-1" pattern="^\+91[6-9]\d{9}$" />
            <select name="status" className="border rounded px-2 py-1">
              <option value="prospect">prospect</option>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="ended">ended</option>
            </select>
            <button disabled={creating} className="rounded bg-slate-900 text-white px-3 py-1 text-xs disabled:opacity-50">
              {creating ? "Creating…" : "Add school"}
            </button>
          </form>
        </div>

        <div className="panel">
          <h2 className="font-medium text-sm mb-2">Camps ({camps.length})</h2>
          {camps.length === 0 ? (
            <div className="text-xs text-slate-400">No camps yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 text-left">
                <tr>
                  <th className="py-1">Date</th>
                  <th>School</th>
                  <th>Status</th>
                  <th>Expected</th>
                  <th>PHRs</th>
                </tr>
              </thead>
              <tbody>
                {camps.map((c) => {
                  const school = schools.find((s) => s.id === c.schoolId);
                  return (
                    <tr key={c.id} className="border-t border-slate-100">
                      <td className="py-1">{c.scheduledDate}</td>
                      <td>
                        <Link to="/camps/$campId" params={{ campId: c.id }} className="text-slate-700 hover:underline">
                          {school?.name ?? c.schoolId.slice(-6)}
                        </Link>
                      </td>
                      <td>
                        <span className="text-xs rounded bg-slate-100 px-1.5">{c.status}</span>
                      </td>
                      <td>{c.expectedChildren ?? "—"}</td>
                      <td>
                        {c.phrsGenerated ?? 0}
                        {c.expectedChildren ? ` / ${c.expectedChildren}` : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <form onSubmit={createCamp} className="mt-4 border-t pt-3 grid gap-2 text-sm">
            <div className="text-xs font-medium text-slate-600">Add camp</div>
            <select name="schoolId" required className="border rounded px-2 py-1">
              <option value="">Select school</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <input name="scheduledDate" type="date" required className="border rounded px-2 py-1" />
            <input
              name="expectedChildren"
              type="number"
              min="1"
              max="2000"
              placeholder="Expected children"
              className="border rounded px-2 py-1"
            />
            <button className="rounded bg-slate-900 text-white px-3 py-1 text-xs">Add camp</button>
          </form>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/schools/")({ component: Schools });
